import React, {useState, useEffect} from 'react'
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
  formatPhoneNumber,
} from 'react-phone-number-input'
import styled from 'styled-components'
import 'react-phone-number-input/style.css'
import {Dispatch, SetStateAction} from 'react'
import type {CountryCode, E164Number} from 'libphonenumber-js'

interface StyledPhoneInputProps {
  isError?: boolean
}

const StyledPhoneInput = styled(PhoneInput)<StyledPhoneInputProps>`
  &.PhoneInput {
    display: flex;
    flex-direction: row-reverse !important;
    border: ${({isError}) =>
      isError ? '1px solid #ef4444' : '1px solid #d1d5db'};
    border-radius: 0.375rem;
    transition: all 0.2s;
    &:focus-within {
      border: 1px solid #000;
    }
  }
  .PhoneInputCountrySelectArrow {
    margin-left: 0.5rem;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid #000; /* Change color as needed */
    border-bottom: 0;
    transform: none !important; /* Remove rotation if any */
    opacity: 1;
  }
  .PhoneInputCountry {
    padding-left: 0.5rem;
    flex-direction: row-reverse;
  }
  .PhoneInputInput {
    width: 100%;
    transition: all 0.2s;
    border-radius: 0.375rem;
    border: none;
    font-size: 0.75rem;
    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }

    &::placeholder {
      font-weight: 100;
    }
  }
`

interface Props {
  error: string | undefined
  isError: boolean | string | undefined
  label: string | undefined
  values: any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldError: (field: string, message: string | undefined) => void
  setIso2: Dispatch<SetStateAction<string>>
  fieldPhoneName?: string
  fieldCountryCodeName?: string
}

const TestPhoneInput = ({
  error,
  isError,
  label,
  values,
  setFieldValue,
  setFieldError,
  setIso2,
  fieldPhoneName = 'phone',
  fieldCountryCodeName = 'country_code',
}: Props) => {
  const isRequired = label?.includes('*')
  const labelText = isRequired ? label?.replace('*', '') : label
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("IL")

  // Format value for PhoneInput - remove leading zero for display in international format
  const formatValueForInput = () => {
    if (!values[fieldPhoneName]) return '';
    
    const phoneWithoutLeadingZero = values[fieldPhoneName].startsWith('0') 
      ? values[fieldPhoneName].substring(1)
      : values[fieldPhoneName];
      
    return `+${values[fieldCountryCodeName]}${phoneWithoutLeadingZero}`;
  }

  return (
    <>
      <div>
        <label htmlFor={'phone'} className="w-full text-right block text-xs">
          {labelText}
          {isRequired && (
            <span className="text-red-600 text-lg font-bold leading-none">
              *
            </span>
          )}
        </label>
        <StyledPhoneInput
          isError={isError}
          defaultCountry="IL"
          country={selectedCountry}
          placeholder="اكتب رقم الهاتف بدون فواصل مثل 0502365478"
          value={formatValueForInput()}
          onChange={(newValue: string | undefined) => {
            if (!newValue) {
              // Clear both fields when input is empty
              setFieldValue(fieldPhoneName, '')
              setFieldValue(fieldCountryCodeName, '')
              return
            }

            // Get the country code for the currently selected country
            const countryCode = getCountryCallingCode(selectedCountry)
            console.log("countryCode", countryCode)
            // Extract phone number (remove the + and country code)
            let phoneNumberOnly = newValue
            
            // If the value starts with a plus sign and country code, extract just the phone number
            if (newValue.startsWith(`+${countryCode}`)) {
              phoneNumberOnly = newValue.substring(countryCode.length + 1) // +1 for the '+' character
            }
            
            // Make sure phone number has a leading zero for local format
            if (phoneNumberOnly && !phoneNumberOnly.startsWith('0')) {
              phoneNumberOnly = '0' + phoneNumberOnly
            }
            
            // Update form values
            setFieldValue(fieldCountryCodeName, countryCode)
            setFieldValue(fieldPhoneName, phoneNumberOnly)
            setIso2(selectedCountry)
          }}
          onCountryChange={(country: CountryCode | undefined) => {
            if (country) {
              setSelectedCountry(country)
              const countryCode = getCountryCallingCode(country)
              setFieldValue(fieldCountryCodeName, countryCode)
              // Keep the existing phone number when changing country
              setIso2(country)
            }
          }}
          inputClassName={`${isError ? 'border-red-500' : 'border-gray-300'}`}
        />
        {isError && (
          <div className="mt-1 text-left text-sm text-red font-semibold">
            {error}
          </div>
        )}
      </div>
    </>
  )
}

export default TestPhoneInput
